import { DataValidation, Worksheet } from 'exceljs';
import { t } from 'i18next';

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';

import { generateSheetNameHash } from '../utils/sheetNameUtils';
import { ExcelColumnDefinition } from '../types';

/**
 * Creates or retrieves a hidden helper sheet for dropdown options
 * Helper sheets are kept hidden to avoid cluttering the Excel workbook UI
 * while still providing the necessary data for dropdowns
 *
 * @param workbook The workbook to create/get the sheet in
 * @param sheetName Name for the helper sheet
 * @returns The helper worksheet
 */
const getOrCreateHelperSheet = (workbook: Worksheet['workbook'], sheetName: string) => {
  let helperSheet = workbook.getWorksheet(sheetName);

  if (!helperSheet) {
    // Create a new hidden sheet if it doesn't exist
    helperSheet = workbook.addWorksheet(sheetName, { state: 'hidden' });
  } else if (helperSheet.state !== 'hidden') {
    // Ensure the sheet is hidden if it already exists but is visible
    helperSheet.state = 'hidden';
  }

  return helperSheet;
};

/**
 * Populates a helper sheet with dropdown options
 *
 * @param helperSheet The helper worksheet to populate
 * @param options Array of options with label and value properties
 */
const populateHelperSheet = (helperSheet: Worksheet, options: Array<{ label: string; value: string }>) => {
  // Add headers for clarity
  helperSheet.getCell('A1').value = 'label';
  helperSheet.getCell('B1').value = 'value';
  helperSheet.getRow(1).font = { bold: true };

  // Add option labels to column A and corresponding values to column B
  options.forEach((option, i) => {
    helperSheet.getCell(`A${i + 2}`).value = option.label;
    helperSheet.getCell(`B${i + 2}`).value = option.value;
  });

  // Adjust column widths for readability
  helperSheet.getColumn('A').width = 30;
  helperSheet.getColumn('B').width = 30;
};

/**
 * Configures a dropdown validation for select/multi_select fields
 *
 * @param columnDefinition The column definition containing options
 * @param worksheet The main worksheet where the validation will be applied
 * @returns DataValidation object for the dropdown
 */
const configureSelectValidation = (columnDefinition: ExcelColumnDefinition, worksheet: Worksheet): DataValidation => {
  const validation: DataValidation = {
    type: 'list',
    formulae: [],
  };

  if (columnDefinition.options && columnDefinition.options.length > 0) {
    const sheetName = generateSheetNameHash(columnDefinition.key);

    // Get or create the helper sheet
    const helperSheet = getOrCreateHelperSheet(worksheet.workbook, sheetName);

    // Populate it with options
    populateHelperSheet(helperSheet, columnDefinition.options);

    // Define the range for dropdown values (skipping the header row)
    const range = `${sheetName}!$A$2:$A$${columnDefinition.options.length + 1}`;

    // Reference the range in the data validation
    validation.formulae = [range];
  } else {
    // Handle cases where options might be missing or empty for a select type
    validation.formulae = ['""']; // Represents an empty list
    logError(new GenericError('Options missing or empty for select type column', { column: columnDefinition.key }));
  }

  return validation;
};

/**
 * Configures validation for basic data types (text, number, date, boolean)
 *
 * @param type The data type to configure validation for
 * @returns DataValidation object for the specified type
 */
const configureBasicValidation = (type: string): DataValidation => {
  const validation: DataValidation = {
    type: 'textLength',
    formulae: [],
    showErrorMessage: true,
  };

  switch (type) {
    case 'text':
      validation.type = 'textLength';
      break;

    case 'monetary':
    case 'number':
      validation.type = 'decimal';
      validation.errorTitle = t('priceCatalogs:validation.errors.number.title');
      validation.error = t('priceCatalogs:validation.errors.number.message');
      break;

    case 'date':
      validation.type = 'date';
      validation.operator = 'greaterThan';
      validation.formulae = ['0']; // Greater than 1st January 1900 (Excel epoch)
      validation.errorTitle = t('priceCatalogs:validation.errors.date.title');
      validation.error = t('priceCatalogs:validation.errors.date.message');
      break;

    case 'boolean':
      validation.type = 'list';
      validation.formulae = ['"TRUE,FALSE"'];
      break;
  }

  return validation;
};

/**
 * Adds validation rules to worksheet columns based on column definitions
 * @param worksheet The Excel worksheet to add validations to
 * @param columns Column definitions to derive validations from
 * @param startRow The row number to start adding validations from
 * @param endRow The row number to end validations at
 */
export const addValidationsToWorksheet = (
  worksheet: Worksheet,
  columns: Array<ExcelColumnDefinition>,
  startRow: number,
  endRow: number,
): void => {
  columns
    .flatMap((column) => (column.columns && column.columns.length > 0 ? column.columns : [column])) // Flatten one level of nested columns
    .forEach((columnDefinition, index) => {
      // Determine the appropriate validation based on column type
      const validation =
        columnDefinition.type === 'select' || columnDefinition.type === 'multi_select'
          ? configureSelectValidation(columnDefinition, worksheet)
          : configureBasicValidation(columnDefinition.type);

      // Add validation to the column (excluding header and instruction rows)
      const columnLetter = worksheet.getColumn(index + 1).letter;
      worksheet.dataValidations.add(`${columnLetter}${startRow}:${columnLetter}${endRow}`, validation);
    });
};
